import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Lightning, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] grain px-4">
      <div className="w-full max-w-md relative z-[2]">
        <Link to="/" className="flex items-center gap-2.5 mb-10 justify-center">
          <div className="w-9 h-9 border border-[#00F0FF] flex items-center justify-center">
            <Lightning size={20} weight="fill" className="text-[#00F0FF]" />
          </div>
          <span className="font-semibold tracking-tight text-lg">MDM Forge</span>
        </Link>

        <div className="border border-[#27272A] bg-[#121214] p-8">
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="w-12 h-12 border border-[#00FF41] flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={24} className="text-[#00FF41]" />
              </div>
              <h1 className="text-xl tracking-tight mb-2">Password updated</h1>
              <p className="text-sm text-[#A1A1AA] font-mono">Redirecting to sign in…</p>
            </div>
          ) : !token ? (
            <div className="text-center">
              <h1 className="text-xl tracking-tight mb-2">Invalid link</h1>
              <p className="text-sm text-[#A1A1AA] font-mono">This reset link is missing its token.</p>
              <Link to="/forgot-password" className="inline-block mt-6 text-sm text-[#00F0FF] hover:underline font-mono">
                Request a new link
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl tracking-tight mb-1">Set new password</h1>
              <p className="text-sm text-[#A1A1AA] mb-6 font-mono">Choose a strong password</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs text-[#A1A1AA] mb-1.5 font-mono">NEW PASSWORD</label>
                  <input
                    data-testid="reset-password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#0A0A0B] border border-[#27272A] px-3 py-2.5 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                    placeholder="min 6 characters"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#A1A1AA] mb-1.5 font-mono">CONFIRM PASSWORD</label>
                  <input
                    data-testid="reset-confirm"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-[#0A0A0B] border border-[#27272A] px-3 py-2.5 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                    placeholder="repeat password"
                  />
                </div>
                {error && (
                  <div data-testid="reset-error" className="text-xs text-[#FF5F56] font-mono border border-[#FF5F56]/30 bg-[#FF5F56]/5 px-3 py-2">
                    {error}
                  </div>
                )}
                <button
                  data-testid="reset-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00F0FF] text-[#0A0A0B] font-medium py-2.5 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
